import { useCallback, useRef } from 'react';
import { getNextLayout } from '../utils/layoutSwitcher';
import { getInitialLayout } from '../layouts/index';

/**
 * Encapsulates the keyboard click logic:
 *   1. onclick  — action dispatcher (space, done, bksp, etc.)
 *   2. handleClick — guard layer (maxLength, layout switching, numeric↔alpha)
 *
 * Key performance contract:
 * - `typed` is read via a ref so it NEVER enters handleClick's dep array.
 *   Without this, every keystroke would rebuild handleClick, causing all
 *   KeyboardRow / KeyboardKey components to re-render even though nothing
 *   visible changed.
 * - handleClick only rebuilds when layout switches (shift/alt toggle) or
 *   when the keyboard type/maxLength changes — both rare events.
 */
export function useKeyClickHandler({
  layout,
  setLayout,
  allLayouts,
  type,
  maxLength,
  typed,
  setKeyboardType,
  onClickBtn,
  closeKeyboard,
  goToSwipe,
  setinputEmpty,
  isAlphaNumericFlag,
  setIsDoneClick,
  deviceInfo,
  screenType,
}) {
  // Always holds the latest typed value without being a dep of handleClick.
  // Safe to read in event handlers (never read during render).
  const typedRef = useRef(typed);
  typedRef.current = typed;
  /**
   * Action dispatcher — handles keys that are not layout switches.
   * Preserved to match the exact behavior of the original onclick().
   */
  const onclick = useCallback(
    f => {
      switch (f) {
        case '{swipe}':
          return (
            deviceInfo?.deviceGroup?.serverAppSettings?.enableLoginWithCard &&
            goToSwipe?.()
          );
        case '{space}':
          return onClickBtn?.(' ');
        case '{tab}':
          return onClickBtn?.('  ');
        case '{done}':
          return isAlphaNumericFlag
            ? (setIsDoneClick?.(true), closeKeyboard?.())
            : closeKeyboard?.();
        case '{cancel}':
          closeKeyboard?.();
          return;
        case '{continue}':
          return onClickBtn?.(f);
        case '{return}':
        case '{enter}':
          return closeKeyboard?.();
        case '{surveyBack}':
          return closeKeyboard?.();
        // NOTE: This case is an intentional JS quirk from the original:
        // when screenType IS 'cashCalculator', the expression evaluates to false,
        // so '{bin}' falls through to default (onClickBtn). Preserved exactly.
        case screenType !== 'cashCalculator' && '{bin}':
          return setinputEmpty?.();
        case '{emptySpace}':
          return;
        default:
          return onClickBtn?.(f);
      }
    },
    [deviceInfo, onClickBtn, goToSwipe, isAlphaNumericFlag, setIsDoneClick, closeKeyboard, setinputEmpty, screenType],
  );

  /**
   * Main click handler — guards maxLength, delegates layout switching,
   * handles numeric↔alpha family crossing.
   */
  const handleClick = useCallback(
    f => {
      // 1. Try layout switching first (shift, alt, special, etc.)
      const nextLayout = getNextLayout(f, layout, allLayouts, type);
      if (nextLayout !== null) {
        setLayout(nextLayout);
        return;
      }

      // 2. Numeric/alpha family crossing: {default} on numpad → switch to text
      if (
        (type === 'number' || type === 'numericAlpha' || type === 'numericAlphaWithoutDone') &&
        f === '{default}'
      ) {
        setKeyboardType?.('textDefaultCaps');
        setLayout(getInitialLayout('textDefaultCaps', allLayouts));
        return;
      }

      // 3. maxLength guard — allow control keys through regardless.
      // Read via ref so typed is NOT a dep: handleClick stays stable across keystrokes.
      const controlKeys = new Set(['{done}', '{bksp}', '{default}', '{lock}', '{shiftActivated}', '{shift}']);
      if (
        f !== '{bin}' &&
        Number(maxLength) > 0 &&
        !controlKeys.has(f) &&
        (String(typedRef.current) || '').split('-').join('').length >= Number(maxLength)
      ) {
        return; // silently ignore input at max length
      }

      // 4. Delegate to action handler
      onclick(f);
    },
    // typed intentionally omitted — read via typedRef at call time
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layout, allLayouts, type, maxLength, setLayout, setKeyboardType, onclick],
  );

  return { handleClick };
}
